import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabaseClient'
import { useOrder, ServiceOption } from '../context/OrderContext'
import { useAuth } from '../context/AuthContext'
import Stepper from "../components/Stepper"
import OrderSummary from "../components/OrderSummary"
import AutocompleteInput from "../components/AutocompleteInput"
import ImageCarousel from "../components/ImageCarousel"
import OptionDescription from "../components/OptionDescription"

// Liste des marques de raquettes populaires
const RACKET_BRANDS = [
  'Babolat',
  'Wilson',
  'Head',
  'Yonex',
  'Victor',
  'Prince',
  'Dunlop',
  'Tecnifibre',
  'Li-Ning',
  'Prokennex',
  'Volkl',
  'Artengo',
  'Carlton',
  'Karakal',
  'Oliver',
  'FZ Forza',
  'Ashaway',
  'Apacs',
  'RSL',
  'Adidas',
  'Nike',
  'Donnay',
  'Pacific',
].sort()

const OptionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { draft, setOption, setRacketInfo, goToStep, canGoToStep } = useOrder()
  const { profile } = useAuth()

  const [options, setOptions] = useState<ServiceOption[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedOption, setSelectedOption] = useState<ServiceOption | null>(null)

  const [racketBrand, setRacketBrand] = useState('')
  const [racketDescription, setRacketDescription] = useState('')
  const [tensionVertical, setTensionVertical] = useState<number | ''>('')
  const [tensionHorizontal, setTensionHorizontal] = useState<number | ''>('')
  const [showTensionHelp, setShowTensionHelp] = useState(false)

  // 🔹 Charger uniquement les options ACTIVES du service
  useEffect(() => {
    if (!draft.service) {
      navigate('/order/start')
      return
    }

    const loadOptions = async () => {
      setLoading(true)

      const { data, error } = await supabase
        .from('service_options')
        .select('*')
        .eq('service_id', draft.service?.id)
        .eq('is_active', true) 
        .order('label')

      if (error) {
        console.error(error)
        setOptions([])
      } else {
        setOptions(data ?? [])
      }

      setLoading(false)
    }

    void loadOptions()
  }, [draft.service, navigate])

  // 🔹 Préremplissage depuis le profil
  useEffect(() => {
    if (!profile) return

    if (profile.default_racket_brand) setRacketBrand(profile.default_racket_brand)
    if (profile.default_tension_vertical)
      setTensionVertical(profile.default_tension_vertical)
    if (profile.default_tension_horizontal)
      setTensionHorizontal(profile.default_tension_horizontal)
    if (profile.default_racket_description)
      setRacketDescription(profile.default_racket_description)
  }, [profile])

  useEffect(() => {
    if (!draft.service || !canGoToStep('OPTIONS')) {
      navigate('/order/start')
      return
    }
    goToStep('OPTIONS')
  }, [draft.service, canGoToStep, goToStep, navigate])

  const handleValidate = () => {
    if (!selectedOption) {
      alert('Veuillez sélectionner une option de cordage.')
      return
    }

    if (!racketBrand || !tensionVertical || !tensionHorizontal) {
      alert('Merci de renseigner toutes les informations obligatoires.')
      return
    }

    setOption(selectedOption)
    setRacketInfo({
      brand: racketBrand,
      description: racketDescription || '',
      tensionVertical: Number(tensionVertical),
      tensionHorizontal: Number(tensionHorizontal),
    })

    navigate('/order/pickup')
  }

  if (loading) {
    return <p>Chargement des options…</p>
  }

  return (
    <div className="card">
      <Stepper />

      <div className="card-header">
        <h1 className="card-title">
          Choisis les options pour ton service "{draft.service?.name}"
        </h1>
      </div>

      {/* --- Options disponibles (ACTIVES UNIQUEMENT) --- */}
      <div className="grid-2" style={{ marginTop: '1rem' }}>
        {options.length === 0 && (
          <p style={{ color: '#9ca3af' }}>
            Aucune option disponible pour ce service.
          </p>
        )}

        {options.map(opt => (
          <button
            key={opt.id}
            type="button"
            data-testid={`option-button-${opt.id}`}
            onClick={() => setSelectedOption(opt)}
            className={`card ${selectedOption?.id === opt.id ? 'card--active' : ''}`}
            style={{
              border:
                selectedOption?.id === opt.id
                  ? '2px solid #22c55e'
                  : '1px solid rgba(148,163,184,0.3)',
              textAlign: 'left',
              cursor: 'pointer',
              padding: 0,
              overflow: 'hidden',
            }}
          >
            {opt.image_urls && opt.image_urls.length > 0 && (
              <div style={{ padding: '0.5rem 0.5rem 0' }}>
                <ImageCarousel images={opt.image_urls} alt={opt.label} height={100} />
              </div>
            )}
            <div style={{ padding: '0.75rem 1rem' }}>
              <div className="card-header" style={{ justifyContent: 'space-between' }}>
                <div>
                  <div className="card-title">{opt.label}</div>
                  <div style={{ fontSize: '.85rem', color: '#9ca3af' }}>
                    {opt.code}
                  </div>
                </div>
                <div style={{ fontWeight: 600, color: '#22c55e' }}>
                  {opt.price_delta >= 0 ? '+' : ''}
                  {opt.price_delta.toFixed(2)} CHF
                </div>
              </div>
              {opt.description && (
                <OptionDescription
                  description={opt.description}
                  testId={`option-description-${opt.id}`}
                />
              )}
            </div>
          </button>
        ))}
      </div>

      {/* --- Informations raquette --- */}
      <div style={{ marginTop: '1.5rem' }}>
        <h2 style={{ fontSize: '1rem', fontWeight: 600 }}>
          Informations sur ta raquette
        </h2>

        <div style={{ marginBottom: '.75rem' }}>
          <label className="label">Marque (obligatoire)</label>
          <AutocompleteInput
            value={racketBrand}
            onChange={setRacketBrand}
            suggestions={RACKET_BRANDS}
            placeholder="Tapez pour rechercher..."
            dataTestId="options-racket-brand"
          />
        </div>

        <div style={{ marginBottom: '.75rem' }}>
          <label className="label">Tension verticale (kg)</label>
          <input
            data-testid="options-tension-vertical"
            className="input"
            type="number"
            min={1}
            max={100}
            value={tensionVertical}
            placeholder="ex: 25"
            onChange={e => setTensionVertical(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>

        <div style={{ marginBottom: '.75rem' }}>
          <label className="label">Tension horizontale (kg)</label>
          <input
            data-testid="options-tension-horizontal"
            className="input"
            type="number"
            min={1}
            max={100}
            value={tensionHorizontal}
            placeholder="ex: 25"
            onChange={e => setTensionHorizontal(e.target.value === '' ? '' : Number(e.target.value))}
          />
        </div>

        <div style={{ marginBottom: '.75rem' }}>
          <button
            type="button"
            data-testid="tension-help-toggle"
            onClick={() => setShowTensionHelp(v => !v)}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              cursor: 'pointer',
              color: '#6366f1',
              fontSize: '.85rem',
              textDecoration: 'underline',
              display: 'flex',
              alignItems: 'center',
              gap: '0.25rem',
            }}
          >
            {showTensionHelp ? '▾' : '▸'} Je ne sais pas quelle tension choisir
          </button>
          {showTensionHelp && (
            <div
              data-testid="tension-help-content"
              style={{
                marginTop: '0.5rem',
                padding: '0.75rem 1rem',
                background: 'rgba(99,102,241,0.08)',
                borderLeft: '3px solid #6366f1',
                borderRadius: '0 6px 6px 0',
                fontSize: '.85rem',
                lineHeight: 1.6,
                color: '#94a3b8',
              }}
            >
              <p style={{ margin: '0 0 0.4rem 0', fontWeight: 600, color: '#c7d2fe' }}>
                Conseils pour choisir votre tension
              </p>
              <ul style={{ margin: 0, paddingLeft: '1.2rem' }}>
                <li><strong>Tennis :</strong> 22–28 kg selon votre niveau (débutant → bas, expert → haut)</li>
                <li><strong>Badminton :</strong> 8–12 kg (loisir) · 12–15 kg (compétition)</li>
                <li><strong>Squash :</strong> 25–30 lbs (11–13 kg)</li>
              </ul>
              <p style={{ margin: '0.5rem 0 0 0' }}>
                En cas de doute, mettez <strong>25 kg</strong> (tennis) ou <strong>10 kg</strong> (badminton) — votre cordeur peut ajuster sur place.
              </p>
            </div>
          )}
        </div>

        <div style={{ marginBottom: '1rem' }}>
          <label className="label">Description (facultatif)</label>
          <textarea
            data-testid="options-racket-description"
            className="input"
            rows={2}
            value={racketDescription}
            onChange={e => setRacketDescription(e.target.value)}
          />
        </div>

        <OrderSummary />

        <button
          type="button"
          data-testid="continue-button"
          className="btn btn-primary"
          onClick={handleValidate}
          style={{ marginTop: '.75rem' }}
        >
          Continuer
        </button>
      </div>
    </div>
  )
}

export default OptionsPage
